import Big from 'big.js';
import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import * as lineitems from 'smg/customer-api/lineitems';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import { DownloadOrderInvoice } from 'smg/customer-api/v2/orders';
import * as models from 'smg/customer-api/models';
import * as orders from 'smg/customer-api/orders';
import { OrderShipments } from 'smg/customer-api/shipments';
import * as session from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { Picture } from 'smg/picture';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { DollarFormat } from 'smg/util';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { ApplyColleagueCopy, OrganizationView, ResolveOrganizationView, WorkPerspective } from '../organization-view';
import * as payments from '../payments';

const FLAG_DELIVERED = 74;
const FLAG_SHIPPED = 112;
const FLAG_SHIPPING_EXCEPTION = 113;

const POLL_MS = 90_000;

type trackState =
    | 'received' |
    'preparing' |
    'production' |
    'shipped' |
    'partial' |
    'delivered' |
    'exception' |
    'notfound';

interface packageView {
    shipmentID: string;
    carrierID: number;
    service: string;
    contents: string;
    trackingNumber: string;
    carrierName: string;
    carrier?: models.Carrier;
    status: string;
    statusDescription: string;
    city: string;
    state: string;
    shippedAt: DateTime;
    deliveredAt: DateTime;
    estimatedDelivery: DateTime;
    events: models.CarrierTrackingEvent[];
    pieces: number;
}

interface derivedView {
    state: trackState;
    step: number;
    eyebrow: string;
    headline: string;
    sub: string;
    heroStat: { label: string; value: string; sub: string; icon: string } | null;
    primary: { label: string; href: string } | null;
    secondary: { label: string; href: string } | null;
    showStages: boolean;
    showException: boolean;
    showCelebration: boolean;
    packageSummary: string;
    packages: packageView[];
    isApo: boolean;
    cancelable: boolean;
}

void main();

async function main() {
    const $page = document.querySelector<HTMLElement>('#order-track-page');
    const $spinner = document.querySelector('#spinner');
    const $foundEl = document.querySelector<HTMLElement>('#order-found-container');
    const $notFoundEl = document.querySelector<HTMLElement>('#order-not-found-container');

    if (!$page || !$foundEl || !$notFoundEl) {
        return;
    }

    const $found = $foundEl;
    const $notFound = $notFoundEl;

    const productPlural = ($page.dataset.productPlural || '').trim();
    const phone = ($page.dataset.phone || '').trim();
    const phoneHref = ($page.dataset.phoneHref || '').trim();
    const email = ($page.dataset.email || '').trim();
    const googleReviews = ($page.dataset.googleReviews || '').trim();
    const productNoun = productPlural || '';

    wireContactLinks(phone, phoneHref, email);

    const orderRef = QueryString().get('id');
    if (!orderRef) {
        showNotFound($spinner, $found, $notFound);
        return;
    }
    const id = orderRef;

    const user = session.Session().then(s => s.user).catch(() => undefined);
    const viewP = user.then(signedIn => {
        if (!signedIn) {
            return new OrganizationView('', []);
        }
        return ResolveOrganizationView(signedIn, { fromURLOnly: true });
    });

    const b = new Batch();
    const carriersPromise = b.get('/carriers', {
        params: { data: 'name,link' },
    }).then(resp => {
        const data = models.CarriersHyejooResponse.fromJSON(resp.data);
        const map = new Map<number, models.Carrier>();
        for (const carrier of data.carriers) {
            map.set(carrier.id, carrier);
        }
        return map;
    });
    const lineItemsPromise = GetLineItems({ data: 'name,pluralName,product' });
    await b.exec();

    const carriers = await carriersPromise;
    const lineItems = await lineItemsPromise;

    let orgView: OrganizationView;
    try {
        orgView = await viewP;
    } catch {
        showNotFound($spinner, $found, $notFound);
        return;
    }

    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let stopPolling = false;
    let lastState: trackState | null = null;
    let lastStep = 0;
    let lastChipKeys = '';
    let balanceWired = false;
    let reorderWired = false;
    let cancelWired = false;
    const expandedJourneys = new Set<string>();

    setupStickyBar();

    async function refresh(isFirst: boolean) {
        try {
            const [order, live] = await Promise.all([
                orders.GetOrder(id, {
                    data: 'id,invoiceNumber,created,userID,customerShipDate,orderStatusID,flagIDs,pending,markedAsShipped,total,paid,balance,orderLineItems,orderProofs,orderShippingOptions',
                    ...orgView.params(),
                }),
                OrderShipments(id, { data: 'id,customsQuantity,tracking', ...orgView.params() }).catch(() => [] as models.HyejooShipment[]),
            ]);

            if (!order) {
                showNotFound($spinner, $found, $notFound);
                stopPolling = true;
                clearPoll();
                return;
            }

            const perspective = orgView.perspective(order.userID);
            const view = deriveView(order, live, carriers, productNoun, perspective);
            await renderPage(order, view, carriers, lineItems, {
                isFirst,
                lastState,
                lastStep,
                lastChipKeys,
                productNoun,
                googleReviews,
                phone,
                phoneHref,
                email,
                id,
                user,
                view: orgView,
                expandedJourneys,
                balanceWired,
                reorderWired,
                cancelWired,
                setBalanceWired: (v: boolean) => {
                    balanceWired = v; 
                },
                setReorderWired: (v: boolean) => {
                    reorderWired = v; 
                },
                setCancelWired: (v: boolean) => {
                    cancelWired = v; 
                },
            });

            lastState = view.state;
            lastStep = view.step;
            lastChipKeys = view.packages.map(p => `${p.shipmentID}:${chipTone(p)}`).join('|');

            if (view.state === 'delivered' && view.packages.every(p => p.deliveredAt.isValid || p.status === 'delivered')) {
                stopPolling = true;
                clearPoll();
            }

            $spinner?.classList.add('d-none');
            $notFound.classList.add('d-none');
            $found.classList.remove('d-none');
        } catch (err) {
            console.error(err);
            if (isFirst) {
                showNotFound($spinner, $found, $notFound);
                stopPolling = true;
                clearPoll();
            }
        }
    }

    function clearPoll() {
        if (pollTimer !== undefined) {
            clearInterval(pollTimer);
            pollTimer = undefined;
        }
    }

    function startPoll() {
        clearPoll();
        if (stopPolling) {
            return;
        }
        pollTimer = setInterval(() => {
            void refresh(false);
        }, POLL_MS);
    }

    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            clearPoll();
            return;
        }
        if (stopPolling) {
            return;
        }
        void refresh(false).then(startPoll);
    });

    await refresh(true);
    if (!stopPolling) {
        startPoll();
    }
}

function showNotFound(
    $spinner: Element | null,
    $found: HTMLElement,
    $notFound: HTMLElement,
) {
    $spinner?.classList.add('d-none');
    $found.classList.add('d-none');
    $notFound.classList.remove('d-none');
}

function wireContactLinks(phone: string, phoneHref: string, email: string) {
    const $notFoundCall = document.querySelector<HTMLAnchorElement>('#not-found-call');
    const $notFoundEmail = document.querySelector<HTMLAnchorElement>('#not-found-email');
    const $contactPhone = document.querySelector<HTMLAnchorElement>('#contact-phone');
    const $contactEmail = document.querySelector<HTMLAnchorElement>('#contact-email');
    const $contactSep = document.querySelector('#contact-sep');

    if ($notFoundCall) {
        if (phoneHref) {
            $notFoundCall.href = phoneHref;
            $notFoundCall.textContent = phone ? `Call ${phone}` : 'Call us';
        } else {
            $notFoundCall.classList.add('d-none');
        }
    }
    if ($notFoundEmail) {
        if (email) {
            $notFoundEmail.href = `mailto:${email}`;
            $notFoundEmail.textContent = 'Email us';
        } else {
            $notFoundEmail.classList.add('d-none');
        }
    }
    if ($contactPhone) {
        if (phone && phoneHref) {
            $contactPhone.href = phoneHref;
            $contactPhone.textContent = phone;
        } else {
            $contactPhone.classList.add('d-none');
        }
    }
    if ($contactEmail && email) {
        $contactEmail.href = `mailto:${email}`;
        $contactEmail.textContent = email;
        $contactEmail.classList.remove('d-none');
        $contactSep?.classList.remove('d-none');
    }
}

function setupStickyBar(): IntersectionObserver | null {
    const $hero = document.querySelector('#hero-block');
    const $sticky = document.querySelector('#sticky-status');
    if (!$hero || !$sticky) {
        return null;
    }
    const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                $sticky.classList.remove('is-stuck');
            } else {
                $sticky.classList.add('is-stuck');
            }
        }
    }, { threshold: 0, rootMargin: '-1px 0px 0px 0px' });
    observer.observe($hero);
    return observer;
}

function hasFlag(flagIDs: number[], flagID: number): boolean {
    return flagIDs.includes(flagID);
}

function formatHugeDate(dt: DateTime): string {
    if (!dt.isValid) {
        return '';
    }
    return dt.toLocaleString(DateTime.DATE_HUGE);
}

function formatShortDate(dt: DateTime): string {
    if (!dt.isValid) {
        return '';
    }
    return dt.toFormat('MMM d');
}

// Carriers return SHOUTING city names ("FAIRFIELD"); title-case for display.
function formatPlace(city: string | undefined, state: string | undefined): string {
    const displayCity = (city || '').toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
    return [displayCity, state].filter(Boolean).join(', ');
}

function formatScanMeta(city: string, state: string, ts: DateTime): string {
    const place = formatPlace(city, state);
    let when = '';
    if (ts.isValid) {
        const now = DateTime.now();
        if (ts.hasSame(now, 'day')) {
            when = `Today, ${ts.toLocaleString(DateTime.TIME_SIMPLE)}`;
        } else {
            when = `${ts.toFormat('MMM d')}, ${ts.toLocaleString(DateTime.TIME_SIMPLE)}`;
        }
    }
    if (place && when) {
        return `${place} · ${when}`;
    }
    return place || when;
}

function carrierLogoURL(carrierID: number): string {
    const $logos = document.getElementById('carrier-logos');
    if (!$logos) {
        return '';
    }
    const map: Record<number, string> = {
        [models.CarrierID.CARRIERS_UPS]: $logos.dataset.ups || '',
        [models.CarrierID.CARRIERS_FED_EX]: $logos.dataset.fedex || '',
        [models.CarrierID.CARRIERS_USPS]: $logos.dataset.usps || '',
        [models.CarrierID.CARRIERS_DHL]: $logos.dataset.dhl || '',
        [models.CarrierID.CARRIERS_AMAZON]: $logos.dataset.amazon || '',
    };
    return map[carrierID] || '';
}

function cleanService(service: string, carrierName: string): string {
    const cleaned = (service || '').replace(/[®™]/g, '').trim();
    // A bare generic word ("express", "ground") tells the customer nothing —
    // the carrier name reads better, same fallback the shipped emails use.
    const generic = /^(express|ground|standard|priority|economy|air|freight)$/i;
    if (!cleaned || generic.test(cleaned)) {
        return carrierName || cleaned || 'Shipment';
    }
    return cleaned;
}

function trackingNumberGroups(trackingNumber: string): string[] {
    const raw = trackingNumber.replace(/\s+/g, '');
    if (!raw) {
        return [];
    }
    if (/^1Z/i.test(raw) && raw.length >= 18) {
        // UPS: 1Z XXX XXX XX XXXX XXXX
        const body = raw.slice(2);
        return [
            raw.slice(0, 2),
            body.slice(0, 3),
            body.slice(3, 6),
            body.slice(6, 8),
            body.slice(8, 12),
            body.slice(12, 16),
        ].filter(Boolean).map(part => part.toUpperCase());
    }
    return raw.match(/.{1,4}/g) || [raw];
}

// The visual digit grouping is pure CSS (margin between group spans) so the
// text content stays the raw number — highlighting and copying it never picks
// up spaces the carrier site would choke on.
function renderTrackingNumber($el: Element, trackingNumber: string) {
    $el.replaceChildren();
    for (const group of trackingNumberGroups(trackingNumber)) {
        const $group = document.createElement('span');
        $group.className = 'order-track__tn-group';
        $group.textContent = group;
        $el.append($group);
    }
}

function isLeadShipment(shipment: models.Shipment): boolean {
    if (!shipment.parentShipmentID || shipment.parentShipmentID === shipment.id) {
        return true;
    }
    return false;
}

function collectPackages(
    order: models.HyejooOrder,
    live: models.HyejooShipment[],
    carriers: Map<number, models.Carrier>,
): packageView[] {
    const liveByID = new Map<string, models.HyejooShipment>();
    for (const row of live) {
        liveByID.set(row.id, row);
    }

    const fromOrder: packageView[] = [];
    for (const orderShippingOption of order.orderShippingOptions) {
        for (const orderShippingOptionShipment of orderShippingOption.orderShippingOptionShipments) {
            const shipment = orderShippingOptionShipment.shipment;
            if (!shipment || !isLeadShipment(shipment)) {
                continue;
            }
            const carrier = carriers.get(shipment.carrierID) || shipment.carrier;
            fromOrder.push(packageFromSources(shipment, liveByID.get(shipment.id), carrier));
        }
    }

    return fromOrder;
}

function packageFromSources(
    shipment: models.Shipment,
    live: models.HyejooShipment | undefined,
    carrier: models.Carrier | undefined,
): packageView {
    const carrierName = carrier?.name || '';
    const service = cleanService(shipment.service || '', carrierName);
    const pieces = live?.customsQuantity || 0;
    return {
        shipmentID: shipment.id,
        carrierID: shipment.carrierID || 0,
        service,
        contents: pieces > 0 ? `${pieces.toLocaleString()} pcs` : '',
        trackingNumber: shipment.trackingNumber || '',
        carrierName,
        carrier,
        status: live?.tracking?.status || '',
        statusDescription: live?.tracking?.statusDescription || '',
        city: live?.tracking?.city || '',
        state: live?.tracking?.state || '',
        shippedAt: shipment.shippedAt,
        deliveredAt: shipment.deliveredAt,
        estimatedDelivery: live?.tracking?.estimatedDelivery || DateTime.invalid('unset'),
        events: live?.tracking?.events || [],
        pieces,
    };
}

function packageMoved(pkg: packageView): boolean {
    return pkg.status === 'picked_up' ||
        pkg.status === 'in_transit' ||
        pkg.status === 'out_for_delivery' ||
        pkg.status === 'in_customs' ||
        pkg.status === 'delivered';
}

function chipTone(pkg: packageView): 'orange' | 'green' | 'gray' | 'warm' {
    if (pkg.status === 'delivered' || pkg.deliveredAt.isValid) {
        return 'green';
    }
    if (pkg.status === 'exception' || pkg.status === 'returned') {
        return 'warm';
    }
    if (pkg.status === 'out_for_delivery' || pkg.status === 'in_transit' || pkg.status === 'picked_up' || pkg.status === 'in_customs') {
        return 'orange';
    }
    if (pkg.status === 'label_ready' || pkg.status === 'pending' || !pkg.status) {
        // A package with a real ship scan is moving even when the carrier's
        // status API still says label-ready (or isn't available at all).
        if (pkg.shippedAt.isValid) {
            return 'orange';
        }
        return 'gray';
    }
    return 'orange';
}

function chipText(pkg: packageView): string {
    if (pkg.status === 'delivered' || pkg.deliveredAt.isValid) {
        const d = pkg.deliveredAt.isValid ? formatShortDate(pkg.deliveredAt) : '';
        return d ? `Delivered ${d}` : 'Delivered';
    }
    if (pkg.status === 'exception' || pkg.status === 'returned') {
        return 'Attention needed';
    }
    if (pkg.status === 'out_for_delivery') {
        return 'Out for delivery';
    }
    if (pkg.status === 'label_ready' || pkg.status === 'pending' || !pkg.status) {
        if (pkg.shippedAt.isValid) {
            const shippedPlace = formatPlace(pkg.city, pkg.state);
            return shippedPlace ? `Moving — ${shippedPlace}` : 'In transit';
        }
        return pkg.trackingNumber ? 'Label created' : 'Pending';
    }
    const place = formatPlace(pkg.city, pkg.state);
    if (place) {
        return `Moving — ${place}`;
    }
    return 'In transit';
}

function deriveView(
    order: models.HyejooOrder,
    live: models.HyejooShipment[],
    carriers: Map<number, models.Carrier>,
    productNoun: string,
    perspective: WorkPerspective,
): derivedView {
    const packages = collectPackages(order, live, carriers);
    const flagIDs = order.flagIDs || [];
    const n = productNoun;
    const colleague = perspective.kind === 'colleague';
    const owner = perspective.kind === 'colleague' ? perspective.ownerName : '';

    const hasException = hasFlag(flagIDs, FLAG_SHIPPING_EXCEPTION);
    const hasDelivered = hasFlag(flagIDs, FLAG_DELIVERED);
    // The shipped flag is recomputed hourly (plus on shipment events), so it
    // can trail a fresh carrier scan — real movement evidence floors the state
    // at shipped, the same signal the flag automation keys on.
    const hasShipped = hasFlag(flagIDs, FLAG_SHIPPED) ||
        packages.some(p => p.shippedAt.isValid || packageMoved(p));

    const deliveredCount = packages.filter(p => p.deliveredAt.isValid || p.status === 'delivered').length;
    const totalPackages = packages.length;
    const isPartial = hasShipped && totalPackages > 0 && deliveredCount > 0 && deliveredCount < totalPackages;
    // The delivered flag can lag the carrier webhook by a beat (or a local DB
    // snapshot can lag prod); when live data shows every package delivered,
    // show delivered — that's the same recompute the flag automation runs.
    const allDelivered = totalPackages > 0 && deliveredCount === totalPackages;

    let state: trackState;
    if (hasException) {
        state = 'exception';
    } else if (hasDelivered || (hasShipped && allDelivered)) {
        state = 'delivered';
    } else if (isPartial) {
        state = 'partial';
    } else if (hasShipped) {
        state = 'shipped';
    } else if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED) {
        state = 'production';
    } else if (order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED) {
        state = 'preparing';
    } else {
        state = 'received';
    }

    const createdDate = formatHugeDate(order.created);
    const firstShipped = earliestValid(packages.map(p => p.shippedAt));
    const lastDelivered = latestValid(packages.map(p => p.deliveredAt));

    let step = 1;
    let eyebrow = createdDate ? `Order placed ${createdDate}` : 'Order received';
    let headline: string;
    let sub: string;
    let packageSummary: string;
    if (colleague) {
        headline = `We've got ${owner}'s order`;
        sub = "A salesperson is reviewing the details now. Nothing else is needed — we'll email the moment the artwork moves to the factory.";
        packageSummary = 'Tracking appears here the moment a carrier scans the label.';
    } else {
        headline = "We've got your order";
        sub = "Your salesperson is reviewing the details now. Nothing else is needed from you — we'll email the moment your artwork moves to the factory.";
        packageSummary = 'Tracking appears here the moment a carrier scans your label.';
    }
    let heroStat: derivedView['heroStat'] = {
        label: 'What happens next',
        value: 'Artwork released within 1 business day',
        sub: 'Then production begins — about 7–10 days',
        icon: 'bi bi-patch-check-fill',
    };
    let primary: derivedView['primary'] = null;
    let secondary: derivedView['secondary'] = null;
    let showStages = false;
    let showException = false;
    let showCelebration = false;
    let cancelable = true;

    if (state === 'preparing') {
        step = 2;
        eyebrow = createdDate ? `Order placed ${createdDate}` : 'Preparing for production';
        if (colleague) {
            headline = n ? `${owner}'s ${n.toLowerCase()} are getting ready` : `${owner}'s order is getting ready`;
            sub = "The approved artwork is on the factory floor and the mold is being cut. This is the longest stretch of the wait — and the part we'll narrate here.";
        } else {
            headline = n ? `Your ${n.toLowerCase()} are getting ready` : 'Your order is getting ready';
            sub = "Your approved artwork is on the factory floor and the mold is being cut. This is the longest stretch of the wait — and the part we'll narrate for you.";
        }
        heroStat = shipDateHero(order);
        showStages = true;
        cancelable = true;
    } else if (state === 'production') {
        step = 2;
        eyebrow = createdDate ? `In production since ${createdDate}` : 'In production';
        if (colleague) {
            headline = n ? `${owner}'s ${n.toLowerCase()} are in production` : `${owner}'s order is in production`;
            sub = "Struck, plated, polished, packed — in that order. Here's exactly where they are today.";
        } else {
            headline = n ? `Your ${n.toLowerCase()} are in production` : 'Your order is in production';
            sub = "Struck, plated, polished, packed — in that order. Here's exactly where yours are today.";
        }
        heroStat = shipDateHero(order);
        showStages = true;
        cancelable = false;
    } else if (state === 'shipped') {
        step = 3;
        eyebrow = firstShipped.isValid ? `Shipped ${formatHugeDate(firstShipped)}` : 'Shipped';
        if (colleague) {
            headline = n ? `${owner}'s ${n.toLowerCase()} are on the way` : `${owner}'s order is on the way`;
            sub = 'Everything on this order has left our facility and is with the carrier now.';
        } else {
            headline = n ? `Your ${n.toLowerCase()} are on the way` : 'Your order is on the way';
            sub = 'Everything you approved has left our facility and is with the carrier now.';
        }
        heroStat = shippingHero(packages, false);
        primary = { label: 'See tracking', href: '#tracking' };
        secondary = { label: 'Download invoice', href: '#invoice' };
        if (packages.length === 1) {
            packageSummary = '1 package — moving. Latest scans first; tap the package for its full journey.';
        } else if (packages.length > 1) {
            packageSummary = `${packages.length} packages — all moving. Latest scans first; tap a package for its full journey.`;
        }
        cancelable = false;
    } else if (state === 'partial') {
        step = 3;
        const firstDelivered = earliestValid(packages.map(p => p.deliveredAt));
        eyebrow = firstDelivered.isValid ?
            `First package delivered ${formatHugeDate(firstDelivered)}` :
            'Partially delivered';
        if (colleague) {
            headline = n ?
                `Some of ${owner}'s ${n.toLowerCase()} are here (${deliveredCount} of ${totalPackages})` :
                `Some of ${owner}'s order is here (${deliveredCount} of ${totalPackages})`;
        } else {
            headline = n ?
                `Some of your ${n.toLowerCase()} are here (${deliveredCount} of ${totalPackages})` :
                `Some of your order is here (${deliveredCount} of ${totalPackages})`;
        }
        sub = 'One or more packages have arrived. The rest are still with the carrier.';
        heroStat = shippingHero(packages, true);
        primary = { label: 'See tracking', href: '#tracking' };
        secondary = { label: 'Download invoice', href: '#invoice' };
        packageSummary = `${totalPackages} packages — ${deliveredCount} delivered, ${totalPackages - deliveredCount} on the way.`;
        cancelable = false;
    } else if (state === 'delivered') {
        step = 4;
        eyebrow = lastDelivered.isValid ? `Delivered ${formatHugeDate(lastDelivered)}` : 'Delivered';
        if (colleague) {
            headline = n ? `${owner}'s ${n.toLowerCase()} are here` : `${owner}'s order is here`;
        } else {
            headline = n ? `Your ${n.toLowerCase()} are here` : 'Your order is here';
        }
        if (totalPackages > 1) {
            sub = 'All packages are signed for. Open one up and tell us it looks as good as the proof did.';
        } else if (colleague) {
            sub = 'The package is signed for. We hope it looks as good as the proof did.';
        } else {
            sub = 'Your package is signed for. Open it up and tell us it looks as good as the proof did.';
        }
        heroStat = {
            label: 'Delivered',
            value: lastDelivered.isValid ? formatHugeDate(lastDelivered) : 'Delivered',
            sub: latestScanSub(packages),
            icon: 'bi bi-check-lg',
        };
        primary = { label: 'Reorder this design', href: '#reorder' };
        secondary = null;
        showCelebration = true;
        packageSummary = totalPackages ?
            `${totalPackages} package${totalPackages === 1 ? '' : 's'} — ${totalPackages === 1 ? 'delivered' : 'all delivered'}.` :
            packageSummary;
        cancelable = false;
    } else if (state === 'exception') {
        step = 3;
        const exceptionPkg = packages.find(p => p.status === 'exception') || packages[0];
        eyebrow = exceptionPkg?.events[0]?.timestamp.isValid ?
            `Delivery attempted ${formatHugeDate(exceptionPkg.events[0].timestamp)}` :
            'Delivery update';
        if (colleague) {
            headline = `There's a hiccup with ${owner}'s delivery`;
        } else {
            headline = "There's a hiccup with your delivery";
        }
        sub = "The carrier couldn't complete one delivery and is holding that carton at the hub. We're already on it — here's the plan.";
        const desc = exceptionPkg?.statusDescription || exceptionPkg?.events[0]?.description || 'Delivery exception';
        heroStat = {
            label: 'What we know',
            value: desc,
            sub: formatPlace(exceptionPkg?.city, exceptionPkg?.state) || 'We\'re watching the next scan',
            icon: 'bi bi-exclamation-circle-fill',
        };
        primary = { label: 'Talk to us about this', href: '#contact' };
        secondary = { label: 'See tracking', href: '#tracking' };
        showException = true;
        const attention = packages.filter(p => p.status === 'exception').length;
        packageSummary = totalPackages ?
            `${totalPackages} package${totalPackages === 1 ? '' : 's'} — ${attention} need${attention === 1 ? 's' : ''} attention.` :
            packageSummary;
        cancelable = false;
    }

    // Stages only when CONFIRMED and the order has no shipments yet (same as the old page).
    if (showStages) {
        let shipmentsCount = 0;
        for (const orderShippingOption of order.orderShippingOptions) {
            shipmentsCount += orderShippingOption.orderShippingOptionShipments.length;
        }
        showStages = order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED &&
            shipmentsCount === 0;
    }

    const isApo = packages.length > 0 && order.orderShippingOptions.some(o =>
        o.shippingOption?.state?.apo === true);

    return {
        state,
        step,
        eyebrow,
        headline,
        sub,
        heroStat,
        primary,
        secondary,
        showStages,
        showException,
        showCelebration,
        packageSummary,
        packages,
        isApo,
        cancelable,
    };
}

function shipDateHero(order: models.HyejooOrder): derivedView['heroStat'] {
    if (!order.customerShipDate.isValid) {
        return null;
    }
    return {
        label: 'Estimated ship date',
        value: `Ships around ${formatHugeDate(order.customerShipDate)}`,
        sub: 'Free UPS Air once it leaves our facility',
        icon: 'bi bi-box-seam-fill',
    };
}

function shippingHero(packages: packageView[], isPartial: boolean): derivedView['heroStat'] {
    const undelivered = packages.filter(p => !(p.deliveredAt.isValid || p.status === 'delivered'));
    const etas = undelivered.
        map(p => p.estimatedDelivery).
        filter(d => d.isValid && d > DateTime.now());
    etas.sort((a, b) => a.toMillis() - b.toMillis());

    const latestScan = latestScanFrom(packages);

    if (etas.length > 0) {
        const soonest = etas[0];
        let sub = latestScan || '';
        if (etas.length > 1 && undelivered.length > 1) {
            sub = sub ?
                `${sub} · first of ${undelivered.length} packages` :
                `First of ${undelivered.length} packages`;
        }
        return {
            label: isPartial ? 'Last package arriving' : 'Arriving',
            value: formatHugeDate(soonest),
            sub,
            icon: 'bi bi-truck',
        };
    }

    if (latestScan) {
        return {
            label: isPartial ? 'Last package' : 'In transit',
            value: latestScan,
            sub: '',
            icon: 'bi bi-truck',
        };
    }

    return null;
}

function latestScanFrom(packages: packageView[]): string {
    let bestLabel = '';
    let bestTs: DateTime = DateTime.invalid('unset');
    for (const pkg of packages) {
        if (pkg.events.length > 0) {
            const event = pkg.events[0];
            if (!bestTs.isValid || (event.timestamp.isValid && event.timestamp > bestTs)) {
                bestTs = event.timestamp;
                const place = formatPlace(event.city || pkg.city, event.state || pkg.state);
                bestLabel = place ?
                    `${event.description} — ${place}` :
                    event.description;
            }
        } else if (pkg.statusDescription) {
            const place = formatPlace(pkg.city, pkg.state);
            bestLabel = place ? `${pkg.statusDescription} — ${place}` : pkg.statusDescription;
        }
    }
    return bestLabel;
}

function latestScanSub(packages: packageView[]): string {
    return latestScanFrom(packages);
}

function earliestValid(dates: DateTime[]): DateTime {
    let best: DateTime = DateTime.invalid('unset');
    for (const d of dates) {
        if (!d.isValid) {
            continue;
        }
        if (!best.isValid || d < best) {
            best = d;
        }
    }
    return best;
}

function latestValid(dates: DateTime[]): DateTime {
    let best: DateTime = DateTime.invalid('unset');
    for (const d of dates) {
        if (!d.isValid) {
            continue;
        }
        if (!best.isValid || d > best) {
            best = d;
        }
    }
    return best;
}

interface renderCtx {
    isFirst: boolean;
    lastState: trackState | null;
    lastStep: number;
    lastChipKeys: string;
    productNoun: string;
    googleReviews: string;
    phone: string;
    phoneHref: string;
    email: string;
    id: string;
    user: Promise<models.User | undefined>;
    view: OrganizationView;
    expandedJourneys: Set<string>;
    balanceWired: boolean;
    reorderWired: boolean;
    cancelWired: boolean;
    setBalanceWired: (v: boolean) => void;
    setReorderWired: (v: boolean) => void;
    setCancelWired: (v: boolean) => void;
}

async function renderPage(
    order: models.HyejooOrder,
    view: derivedView,
    carriers: Map<number, models.Carrier>,
    lineItems: Map<string, models.LineItem>,
    ctx: renderCtx,
) {
    const setText = (sel: string, text: string) => {
        const $el = document.querySelector(sel);
        if ($el && $el.textContent !== text) {
            $el.textContent = text;
        }
    };

    const $orderOwner = document.querySelector('#order-owner') as HTMLElement | null;
    const $orderOwnerName = document.querySelector('#order-owner-name') as HTMLElement | null;
    if (ctx.view.active && $orderOwner && $orderOwnerName) {
        $orderOwnerName.textContent = ctx.view.detailLabel(order.userID, 'order');
        $orderOwner.classList.remove('d-none');
    } else {
        $orderOwner?.classList.add('d-none');
    }

    ApplyColleagueCopy(document, ctx.view.perspective(order.userID));

    setText('#eyebrow', view.eyebrow);
    setText('#headline', view.headline);
    setText('#sub', view.sub);
    setText('#invoice-number', String(order.invoiceNumber || ''));
    setText('#package-summary', view.packageSummary);

    // Hero stat
    const $heroStat = document.querySelector('#hero-stat');
    if ($heroStat) {
        if (view.heroStat) {
            $heroStat.classList.remove('d-none');
            if (!ctx.isFirst && ctx.lastState !== view.state) {
                $heroStat.classList.add('updating');
                setTimeout(() => $heroStat.classList.remove('updating'), 280);
            }
            setText('#hero-stat-label', view.heroStat.label);
            setText('#hero-stat-value', view.heroStat.value);
            setText('#hero-stat-sub', view.heroStat.sub);
            const $icon = document.querySelector('#hero-stat-icon i');
            if ($icon) {
                $icon.className = view.heroStat.icon;
            }
        } else {
            $heroStat.classList.add('d-none');
        }
    }

    // Sticky bar
    const $sticky = document.querySelector('#sticky-status');
    const $stickyLine = document.querySelector('#sticky-line');
    const $stickyPrimary = document.querySelector<HTMLAnchorElement>('#sticky-primary');
    if ($sticky && $stickyLine) {
        const stickyText = view.heroStat ?
            `${view.heroStat.label} ${view.heroStat.value}` :
            view.headline;
        $stickyLine.textContent = stickyText;
        const stickyPrimary = view.primary;
        if (stickyPrimary && $stickyPrimary) {
            $stickyPrimary.textContent = stickyPrimary.label;
            $stickyPrimary.href = stickyPrimary.href;
            $stickyPrimary.classList.remove('d-none');
            if (stickyPrimary.href === '#tracking') {
                $stickyPrimary.onclick = e => {
                    e.preventDefault();
                    document.querySelector('#tracking')?.scrollIntoView({ behavior: 'smooth' });
                };
            } else if (stickyPrimary.href === '#reorder') {
                $stickyPrimary.onclick = e => {
                    e.preventDefault();
                    document.querySelector<HTMLButtonElement>('#order-reorder')?.click();
                };
            } else if (stickyPrimary.href === '#contact') {
                $stickyPrimary.onclick = e => {
                    e.preventDefault();
                    if (ctx.phoneHref) {
                        window.location.href = ctx.phoneHref;
                    }
                };
            } else {
                $stickyPrimary.onclick = null;
            }
        } else {
            $stickyPrimary?.classList.add('d-none');
        }
        if (ctx.isFirst) {
            $sticky.classList.add('aa-rise');
        }
    }

    // Stepper
    renderStepper(view.step, ctx.lastStep, view.state === 'delivered' && ctx.isFirst);

    // Step dates under filled steps
    const stepDates = [
        order.created,
        order.created, // production date approx
        earliestValid(view.packages.map(p => p.shippedAt)),
        latestValid(view.packages.map(p => p.deliveredAt)),
    ];
    document.querySelectorAll<HTMLElement>('.order-track__step').forEach(($step, i) => {
        const $date = $step.querySelector('.order-track__step-date');
        if (!$date) {
            return;
        }
        const n = i + 1;
        const filled = n < view.step || (n === view.step && (view.step === 1 || view.step === 3 || view.step === 4));
        $date.textContent = filled && stepDates[i]?.isValid ? formatShortDate(stepDates[i]) : '';
    });

    // Stages
    const $stagesCard = document.querySelector('#stages-card');
    const $stagesContainer = document.querySelector('#stages-container');
    if ($stagesCard && $stagesContainer) {
        if (view.showStages) {
            $stagesCard.classList.remove('d-none');
            $stagesContainer.replaceChildren();
            for (const lineItem of order.orderLineItems) {
                if (lineItem.lineItemModel?.shipping || lineItem.lineItemModel?.tax) {
                    continue;
                }
                const stages = lineItem.orderLineItemStages;
                for (let i = 0; i < stages.length; i++) {
                    const stage = stages[i];
                    const isLast = i === stages.length - 1;
                    const done = !isLast && stage.stop.isValid;
                    const active = isLast;
                    const $row = template.Clone('#tpl-stage-row');
                    const $dot = $row.querySelector('.order-track__stage-dot');
                    const $name = $row.querySelector('.order-track__stage-name');
                    const $when = $row.querySelector('.order-track__stage-when');
                    if ($dot) {
                        if (done || active) {
                            $dot.classList.add('is-filled');
                            $dot.textContent = done ? '✓' : '';
                        }
                    }
                    if ($name) {
                        $name.textContent = stage.stage?.name || '';
                        if (active) {
                            $name.classList.add('fw-bold');
                        }
                        if (!done && !active) {
                            $name.classList.add('text-muted');
                        }
                    }
                    if ($when) {
                        if (done && stage.stop.isValid) {
                            $when.textContent = formatShortDate(stage.stop);
                        } else if (active) {
                            $when.textContent = 'in progress';
                        }
                    }
                    $stagesContainer.append($row);
                }
                break; // first product line item's stages
            }
        } else {
            $stagesCard.classList.add('d-none');
        }
    }

    // CTAs
    const $ctas = document.querySelector('#hero-ctas');
    const $primary = document.querySelector<HTMLAnchorElement>('#primary-cta');
    const $secondary = document.querySelector<HTMLAnchorElement>('#secondary-cta');
    if ($ctas && $primary && $secondary) {
        const primary = view.primary;
        const secondary = view.secondary;
        if (primary) {
            $ctas.classList.remove('d-none');
            $primary.textContent = primary.label;
            $primary.href = primary.href;
            $primary.classList.remove('d-none');
            if (primary.href === '#tracking') {
                $primary.onclick = e => {
                    e.preventDefault();
                    document.querySelector('#tracking')?.scrollIntoView({ behavior: 'smooth' });
                };
            } else if (primary.href === '#reorder') {
                $primary.onclick = e => {
                    e.preventDefault();
                    document.querySelector<HTMLButtonElement>('#order-reorder')?.click();
                };
            } else if (primary.href === '#contact') {
                $primary.onclick = e => {
                    e.preventDefault();
                    if (ctx.phoneHref) {
                        window.location.href = ctx.phoneHref;
                    }
                };
            } else {
                $primary.onclick = null;
            }
            if (secondary) {
                $secondary.textContent = secondary.label;
                $secondary.href = secondary.href;
                $secondary.classList.remove('d-none');
                if (secondary.href === '#invoice') {
                    $secondary.onclick = e => {
                        e.preventDefault();
                        document.querySelector<HTMLButtonElement>('#download-invoice')?.click();
                    };
                } else if (secondary.href === '#tracking') {
                    $secondary.onclick = e => {
                        e.preventDefault();
                        document.querySelector('#tracking')?.scrollIntoView({ behavior: 'smooth' });
                    };
                } else {
                    $secondary.onclick = null;
                }
            } else {
                $secondary.classList.add('d-none');
            }
        } else {
            $ctas.classList.add('d-none');
        }
    }

    // Exception / celebration
    const $exception = document.querySelector('#exception-card');
    if ($exception) {
        if (view.showException) {
            $exception.classList.remove('d-none');
            const exceptionPkg = view.packages.find(p => p.status === 'exception');
            const narrative = exceptionPkg?.statusDescription ||
                "The carrier reported a delivery exception. Nothing is lost — we're on it with them now.";
            setText('#exception-narrative', narrative);
        } else {
            $exception.classList.add('d-none');
        }
    }
    const $celebration = document.querySelector('#celebration-card');
    const $review = document.querySelector<HTMLAnchorElement>('#leave-review-btn');
    if ($celebration) {
        if (view.showCelebration) {
            $celebration.classList.remove('d-none');
            if ($review && ctx.googleReviews) {
                $review.href = ctx.googleReviews;
                $review.classList.remove('d-none');
            } else {
                $review?.classList.add('d-none');
            }
        } else {
            $celebration.classList.add('d-none');
        }
    }

    // Packages
    renderPackages(view, ctx);

    // APO
    const $apo = document.querySelector('#apo-notice');
    if ($apo) {
        if (view.isApo) {
            $apo.classList.remove('d-none');
        } else {
            $apo.classList.add('d-none');
        }
    }

    // Line items
    await renderLineItems(order, lineItems);

    // Totals
    await renderTotals(order, ctx);

    // Footer actions
    wireFooter(order, view, ctx);
}

function renderStepper(step: number, lastStep: number, celebrate: boolean) {
    document.querySelectorAll<HTMLElement>('.order-track__step').forEach($step => {
        const n = Number($step.dataset.step);
        const done = n < step || (n === step && (step === 1 || step === 3 || step === 4));
        const current = n === step;
        const filled = n < step || done;

        $step.classList.toggle('is-filled', filled);
        $step.classList.toggle('is-current', current);
        $step.classList.toggle('is-pending', !filled);

        const $mark = $step.querySelector('.order-track__mark');
        if ($mark) {
            $mark.textContent = filled ? '✓' : String(n);
        }

        const $circle = $step.querySelector('.order-track__circle');
        if ($circle && current && (celebrate || (lastStep > 0 && n === step && step > lastStep))) {
            $circle.classList.add('aa-pop');
            $circle.addEventListener('animationend', () => $circle.classList.remove('aa-pop'), { once: true });
        }

        // bars: before is orange when n <= step (except first), after when n < step (except last)
        const $before = $step.querySelector('.order-track__bar--before');
        const $after = $step.querySelector('.order-track__bar--after');
        if ($before) {
            $before.classList.toggle('is-hot', n > 1 && n <= step);
        }
        if ($after) {
            $after.classList.toggle('is-hot', n < 4 && n < step);
        }
    });
}

function renderPackages(
    view: derivedView,
    ctx: renderCtx,
) {
    const $container = document.querySelector('#packages-container');
    const $empty = document.querySelector('#no-packages-yet');
    if (!$container || !$empty) {
        return;
    }

    if (view.packages.length === 0) {
        $container.classList.add('d-none');
        $container.replaceChildren();
        $empty.classList.remove('d-none');
        return;
    }

    $empty.classList.add('d-none');
    $container.classList.remove('d-none');
    $container.replaceChildren();

    for (const pkg of view.packages) {
        const $card = template.Clone('#tpl-package-card');
        const tone = chipTone(pkg);

        const $service = $card.querySelector('.package-service');
        const $contents = $card.querySelector('.package-contents');
        const $chip = $card.querySelector('.package-chip');
        const $tracking = $card.querySelector('.package-tracking');
        const $copy = $card.querySelector<HTMLButtonElement>('.package-copy');
        const $link = $card.querySelector<HTMLAnchorElement>('.package-carrier-link');
        const $dot = $card.querySelector('.package-dot');
        const $latestLabel = $card.querySelector('.package-latest-label');
        const $latestMeta = $card.querySelector('.package-latest-meta');
        const $toggle = $card.querySelector<HTMLButtonElement>('.package-toggle');
        const $timeline = $card.querySelector('.package-timeline');

        if ($service) {
            $service.textContent = pkg.service;
        }

        const $logo = $card.querySelector<HTMLImageElement>('.package-carrier-logo');
        if ($logo) {
            const logoURL = carrierLogoURL(pkg.carrierID);
            if (logoURL) {
                $logo.src = logoURL;
                $logo.alt = pkg.carrierName;
                $logo.classList.remove('d-none');
            }
        }
        if ($contents) {
            if (pkg.contents) {
                $contents.textContent = pkg.contents;
                $contents.classList.remove('d-none');
            } else {
                $contents.classList.add('d-none');
            }
        }
        if ($chip) {
            $chip.textContent = chipText(pkg);
            $chip.classList.add(`order-track__chip--${tone}`);
        }
        if ($tracking) {
            renderTrackingNumber($tracking, pkg.trackingNumber);
        }
        if ($copy) {
            if (!pkg.trackingNumber) {
                $copy.classList.add('d-none');
            } else {
                $copy.addEventListener('click', () => {
                    void navigator.clipboard.writeText(pkg.trackingNumber.replace(/\s+/g, '')).catch(() => undefined);
                    $copy.textContent = 'Copied!';
                    setTimeout(() => {
                        $copy.textContent = 'Copy'; 
                    }, 1600);
                });
            }
        }
        if ($link) {
            const trackingNumber = pkg.trackingNumber.replace(/\s+/g, '');
            if (trackingNumber && pkg.carrier?.link) {
                $link.href = pkg.carrier.trackingURL(trackingNumber);
                const host = carrierHostLabel(pkg.carrierName, pkg.carrier.link);
                $link.textContent = host;
            } else {
                $link.classList.add('d-none');
            }
        }
        if ($dot) {
            $dot.classList.add(`order-track__scan-dot--${tone}`);
        }

        const latestEvent = pkg.events[0];
        if ($latestLabel) {
            let fallbackLabel: string;
            if (pkg.deliveredAt.isValid) {
                fallbackLabel = 'Delivered';
            } else if (pkg.shippedAt.isValid) {
                fallbackLabel = 'On its way';
            } else if (pkg.trackingNumber) {
                fallbackLabel = 'Awaiting first scan';
            } else {
                fallbackLabel = 'Tracking pending';
            }
            $latestLabel.textContent = latestEvent?.description ||
                pkg.statusDescription ||
                fallbackLabel;
        }
        if ($latestMeta) {
            if (latestEvent) {
                $latestMeta.textContent = formatScanMeta(
                    latestEvent.city || pkg.city,
                    latestEvent.state || pkg.state,
                    latestEvent.timestamp,
                );
            } else if (pkg.deliveredAt.isValid) {
                $latestMeta.textContent = formatScanMeta(pkg.city, pkg.state, pkg.deliveredAt);
            } else if (pkg.shippedAt.isValid) {
                $latestMeta.textContent = formatScanMeta(pkg.city, pkg.state, pkg.shippedAt);
            } else {
                $latestMeta.textContent = formatScanMeta(pkg.city, pkg.state, DateTime.invalid('unset'));
            }
        }

        if ($toggle && $timeline) {
            if (pkg.events.length === 0) {
                $toggle.classList.add('d-none');
            } else {
                const expanded = ctx.expandedJourneys.has(pkg.shipmentID);
                if (expanded) {
                    $timeline.classList.remove('d-none');
                    $toggle.textContent = 'Hide journey';
                }
                for (const event of pkg.events) {
                    const $scan = template.Clone('#tpl-scan-row');
                    const $label = $scan.querySelector('.scan-label');
                    const $meta = $scan.querySelector('.scan-meta');
                    if ($label) {
                        $label.textContent = event.description;
                    }
                    if ($meta) {
                        $meta.textContent = formatScanMeta(event.city, event.state, event.timestamp);
                    }
                    $timeline.append($scan);
                }
                $toggle.addEventListener('click', () => {
                    const open = !$timeline.classList.contains('d-none');
                    if (open) {
                        $timeline.classList.add('d-none');
                        $toggle.textContent = 'Full journey';
                        ctx.expandedJourneys.delete(pkg.shipmentID);
                    } else {
                        $timeline.classList.remove('d-none');
                        $toggle.textContent = 'Hide journey';
                        ctx.expandedJourneys.add(pkg.shipmentID);
                    }
                });
            }
        }

        $container.append($card);
    }
}

function carrierHostLabel(name: string, link: string): string {
    const lower = name.toLowerCase();
    if (lower.includes('ups')) {
        return 'Open on ups.com';
    }
    try {
        const host = new URL(link.replace('%%', 'x')).hostname.replace(/^www\./, '');
        if (host) {
            return `Open on ${host}`;
        }
    } catch {
        // fall through
    }
    return name ? `Track on ${name}` : 'Track package';
}

async function renderLineItems(
    order: models.HyejooOrder,
    lineItems: Map<string, models.LineItem>,
) {
    const $container = document.querySelector('#line-items-container');
    if (!$container) {
        return;
    }
    $container.replaceChildren();

    const proofs = order.orderProofs || [];
    let proofIndex = 0;

    for (const orderLineItem of order.orderLineItems) {
        if (!(orderLineItem.lineItemModel?.product || !orderLineItem.lineItemModel)) {
            continue;
        }
        if (orderLineItem.lineItemModel?.shipping || orderLineItem.lineItemModel?.tax) {
            continue;
        }

        const $row = template.Clone('#tpl-line-item');
        const lineItem = lineItems.get(orderLineItem.lineItemID) || orderLineItem.lineItemModel;
        const name = (await lineitems.LineItemMap()).get(orderLineItem.lineItemID)?.networkNamePlural() ||
            orderLineItem.lineItem ||
            lineItem?.pluralName ||
            lineItem?.name ||
            'Item';
        const typeName = (await lineitems.TypeMap()).get(orderLineItem.lineItemTypeID)?.networkName() ||
            orderLineItem.type ||
            '';
        const sizeName = (await lineitems.SizeMap()).get(orderLineItem.lineItemTypeSizeID)?.networkName() ||
            orderLineItem.size ||
            '';

        let title = `${orderLineItem.quantity.toLocaleString()} × ${name}`;
        if (sizeName) {
            title += ` — ${sizeName}`;
        } else if (typeName) {
            title += ` — ${typeName}`;
        }

        const $title = $row.querySelector('.line-title');
        if ($title) {
            $title.textContent = title;
        }

        const $options = $row.querySelector('.line-options');
        if ($options) {
            for (const child of orderLineItem.orderChildLineItems) {
                const { label, optionName } = orderChildLineItemLabel(child);
                if (!label || optionName.toLowerCase() === 'default') {
                    continue;
                }
                const $opt = document.createElement('div');
                $opt.className = 'small text-muted';
                $opt.textContent = `— ${label}`;
                $options.append($opt);
            }
        }

        const proof = proofs[proofIndex] || proofs[0];
        proofIndex++;
        const $img = $row.querySelector<HTMLImageElement>('.line-proof-img');
        const $link = $row.querySelector<HTMLAnchorElement>('.order-track__proof-link');
        if (proof?.proof && $img && $link) {
            // getURLs() derives from proof.file; the endpoint may only send the
            // server-built main/thumbnail, so those are the fallback.
            const derived = proof.proof.getURLs();
            const main = derived.main || proof.proof.main || '';
            const thumb = derived.thumbnail || proof.proof.thumbnail || '';
            $link.dataset.caption = proof.proof.name || proof.name || '';
            if (main) {
                $link.href = main;
                void Picture($img, main, {
                    displayWidth: 132,
                    alt: 'Approved proof',
                    fancyboxThumb: true,
                    pdfSrc: proof.proof.url() || undefined,
                });
                const $picture = $img.parentElement;
                if ($picture instanceof HTMLPictureElement) {
                    $picture.classList.add('d-block', 'w-100');
                }
            } else if (thumb) {
                $img.src = thumb;
                $link.href = thumb;
            } else {
                $link.classList.add('d-none');
                $link.parentElement?.querySelector('p')?.classList.add('d-none');
            }
        } else if ($link) {
            $link.classList.add('d-none');
            $link.parentElement?.querySelector('p')?.classList.add('d-none');
        }

        $container.append($row);
    }
}

async function renderTotals(
    order: models.HyejooOrder,
    ctx: renderCtx,
) {
    const $rows = document.querySelector('#total-rows');
    if (!$rows) {
        return;
    }
    $rows.replaceChildren();

    let shippingTotalNum = Big('0');
    let taxTotalNum = Big('0');
    let discountsNum = Big('0');
    let totalNum = Big('0');

    for (const lineItem of order.orderLineItems) {
        if (lineItem.price.lt(0)) {
            discountsNum = discountsNum.add(lineItem.price);
        }

        const lineSubtotal = lineItem.price.mul(lineItem.quantity);
        totalNum = totalNum.add(lineSubtotal);

        if (!lineItem.lineItemModel?.shipping && !lineItem.lineItemModel?.tax) {
            const label = [
                (await lineitems.LineItemMap()).get(lineItem.lineItemID)?.networkNamePlural() || lineItem.lineItem,
                (await lineitems.TypeMap()).get(lineItem.lineItemTypeID)?.networkName() || lineItem.type,
                (await lineitems.SizeMap()).get(lineItem.lineItemTypeSizeID)?.networkName() || lineItem.size,
                lineItem.description,
            ].filter(Boolean).join(' — ');

            const amount = lineItem.price.mul(lineItem.quantity);
            appendTotalRow($rows, `${lineItem.quantity.toLocaleString()} × ${label}`, DollarFormat(amount));

            for (const childLineItem of lineItem.orderChildLineItems) {
                const childSubtotal = childLineItem.price.mul(childLineItem.quantity);
                totalNum = totalNum.add(childSubtotal);
                if (childLineItem.price.lt(0)) {
                    discountsNum = discountsNum.add(childLineItem.price);
                }
                if (childLineItem.price.gt(0)) {
                    const { label } = orderChildLineItemLabel(childLineItem);
                    appendTotalRow($rows, label || 'Option', DollarFormat(childSubtotal), true);
                }
            }
        } else if (lineItem.lineItemModel?.shipping) {
            shippingTotalNum = shippingTotalNum.add(lineItem.price);
        } else {
            taxTotalNum = taxTotalNum.add(lineItem.price);
        }
    }

    if (discountsNum.lt(0)) {
        appendTotalRow($rows, 'Discounts', DollarFormat(discountsNum));
    }
    if (shippingTotalNum.gt(0)) {
        appendTotalRow($rows, 'Shipping', DollarFormat(shippingTotalNum));
    }
    if (taxTotalNum.gt(0)) {
        appendTotalRow($rows, 'Tax', DollarFormat(taxTotalNum));
    }

    setTextContent('#total', DollarFormat(order.total));

    const paid = order.paid;
    const $paidRow = document.querySelector('#paid-row');
    if ($paidRow && paid.gt(0)) {
        $paidRow.classList.remove('d-none');
        setTextContent('#paid-amount', `− ${DollarFormat(paid)}`);
    } else {
        $paidRow?.classList.add('d-none');
    }

    const $balance = document.querySelector('#remaining-balance');
    const $payBtn = document.querySelector<HTMLButtonElement>('#pay-balance-btn');
    if ($balance && order.balance.gte('0.05')) {
        $balance.classList.remove('d-none');
        setTextContent('#balance', DollarFormat(order.balance));
        if ($payBtn && !ctx.balanceWired) {
            ctx.setBalanceWired(true);
            $payBtn.addEventListener('click', async () => {
                await payments.PromptPayment(await ctx.user, [order.id], order.balance).
                    then(() => {
                        window.location.reload();
                        toast.Success('Thank you for your payment');
                    });
            });
        }
    } else {
        $balance?.classList.add('d-none');
    }
}

function appendTotalRow($rows: Element, label: string, amount: string, isChild = false) {
    const $row = template.Clone('#tpl-total-row');
    const $label = $row.querySelector('.total-label');
    const $amount = $row.querySelector('.total-amount');
    if (isChild) {
        $row.firstElementChild?.classList.add('small', 'ps-3');
    }
    if ($label) {
        $label.textContent = label;
    }
    if ($amount) {
        $amount.textContent = amount;
    }
    $rows.append($row);
}

// Mirrors OrderChildLineItem.String() in Go: "Child - Option (Description)",
// with each part falling back through its network-name overrides.
function orderChildLineItemLabel(childLineItem: models.OrderChildLineItem): { label: string; optionName: string } {
    const childName = childLineItem.child ||
        childLineItem.childLineItem?.networkName() ||
        '';
    const optionName = childLineItem.option ||
        childLineItem.childLineItemOption?.networkName() ||
        '';
    let label = childName;
    if (childName && optionName && optionName.toLowerCase() !== 'default') {
        label += ' - ';
    }
    if (optionName.toLowerCase() !== 'default') {
        label += optionName;
    }
    if (childLineItem.description) {
        label = label ? `${label} (${childLineItem.description})` : childLineItem.description;
    }
    return { label, optionName };
}

function setTextContent(sel: string, text: string) {
    const $el = document.querySelector(sel);
    if ($el) {
        $el.textContent = text;
    }
}

function wireFooter(
    order: models.HyejooOrder,
    view: derivedView,
    ctx: renderCtx,
) {
    const canManage = ctx.view.canManage(order.userID);

    const $reorder = document.querySelector<HTMLButtonElement>('#order-reorder');
    if ($reorder && !ctx.reorderWired) {
        ctx.setReorderWired(true);
        if (order.total.eq(0)) {
            $reorder.classList.add('d-none');
        } else {
            $reorder.addEventListener('click', () => {
                buttons.Start($reorder);
                void carts.AddCartItem({ orderID: ctx.id }, ctx.view.cartOptions()).then(() => {
                    $reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                    $reorder.classList.add('btn-outline-success');
                    $reorder.textContent = 'Added to cart';
                }).finally(() => buttons.Stop($reorder));
            });
        }
    }

    const $share = document.querySelector<HTMLAnchorElement>('#order-share');
    if ($share) {
        if (!canManage) {
            $share.classList.add('d-none');
        } else {
            $share.classList.remove('d-none');
            const subject = 'All About Order Details';
            const body = `Here's a link to your order details:\n\n${window.location.href}`;
            $share.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }
    }

    const $invoice = document.querySelector<HTMLButtonElement>('#download-invoice');
    if ($invoice) {
        $invoice.textContent = order.invoiceNumber ?
            `Download invoice #${order.invoiceNumber} (PDF)` :
            'Download invoice (PDF)';
        // A key keeps its bearer read; an id or invoice number in the URL reads
        // the loaded order by its id, the one-order read the invoice needs.
        const search = orders.OrderRefSearch(ctx.id, 'id');
        const q = search.keyed ? search.q : `id:${order.id ?? ''}`;
        $invoice.onclick = () => {
            buttons.Start($invoice);
            void DownloadOrderInvoice(q, ctx.view.params().organization).catch(ShowError).finally(() => buttons.Stop($invoice));
        };
    }

    const $cancel = document.querySelector<HTMLButtonElement>('#order-cancel');
    if ($cancel) {
        if (canManage && view.cancelable && orders.OrderIsCancelable(order) && (order.id || ctx.id)) {
            $cancel.classList.remove('d-none');
            if (!ctx.cancelWired) {
                ctx.setCancelWired(true);
                $cancel.addEventListener('click', () => {
                    void orders.PromptCancelOrder(order.id || ctx.id || '', {
                        redirectTo: Relref('/orders'),
                    });
                });
            }
        } else {
            $cancel.classList.add('d-none');
        }
    }
}
